import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { CountryCode } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { SharedGridStyleNoPadding } from "features/CenterContent/shared/MainContentGrid";
import { HeadedContainer } from "features/CenterContent/VehicleDetails/shared/HeadedContainer";
import { TrainRouteHeader } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainRouteHeader";
import { TrainStopContent } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/TrainStopContent";
import {
  headerWidths,
  TrainStopHeader,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/TrainStopHeader";
import {
  TdPaddingBottomPx,
  TdPaddingSidePx,
  TdPaddingTopPx,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/TrainStopRow";
import { FillrateResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/types";
import { TrainRouteResponse } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { Box, Table, TableBody, TableHeader } from "shared/components/Spor";
import { sum } from "shared/utils/arrayUtils";
import styled, { useTheme } from "styled-components";

// xs doesnt exist in Spor, therefore we must make it ourself
export const TableXS = styled(Table)`
  table-layout: fixed;
  th {
    padding: 6px;
  }

  tr:last-child > td {
    border-color: ${({ theme }) => theme.colorBackgroundMain};
    height: fill-available;
  }

  td {
    padding: ${TdPaddingTopPx}px ${TdPaddingSidePx}px ${TdPaddingBottomPx}px
      ${TdPaddingSidePx}px;
    height: 0; /* Same as min-content, but enables 'x%' height for items inside td */
  }

  th,
  td {
    border-color: ${({ theme }) => theme.colorBackgroundSecondary};

    &:first-child {
      padding-left: 18px;
    }
  }
`;

type TrainRouteProps = {
  trainRoute: UseQueryResult<TrainRouteResponse>;
  trainNumber?: string;
  nominalDate?: string;
  countryCode?: CountryCode;
};

const TrainRoute: FC<TrainRouteProps> = ({
  trainNumber,
  nominalDate,
  countryCode,
  trainRoute,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data, status } = trainRoute;
  const { data: fillRate } = useQuery({
    queryKey: ["fillRate", trainNumber, nominalDate],
    refetchOnWindowFocus: false,
    retry: 1,
    refetchInterval: 60000,
    queryFn: ({ signal }) =>
      queryFnGET<FillrateResponse>({
        signal,
        url: `${getBackendUrl()}/fillrate?trainId=${trainNumber}&nominalDate=${nominalDate}`,
      }),
    enabled: countryCode === "NO",
  });
  const [delay, setDelay] = useState<number | null>(null);

  useEffect(() => {
    const hasData = status === "success" && data && data.stops.length > 0;
    if (!hasData) {
      setDelay(null);
      return;
    }
    const currentStop = data.stops.findLast((stop) => !!stop.isArrived);
    setDelay(currentStop?.departureDelay || currentStop?.arrivalDelay || null);
  }, [data, status]);

  const getContent = () => {
    switch (status) {
      case "pending":
        return <SkeletonLoader skeletonType="trainStops" />;
      case "success": {
        if (data && data.stops.length > 0) {
          // "Stasjon" uses "auto" as width. We want to ensure that it gets atleast 100 width, else it looks way off
          const minWidthAuto = 100;
          const tableWidth =
            sum(headerWidths.filter((it) => it !== undefined)) + minWidthAuto;
          const tableWidthPx = `${tableWidth}px`;
          return (
            <Box
              flex="1"
              overflow="auto"
              height="100vh"
              pl="12px"
              pr="12px"
              pt="12px"
            >
              <TableXS
                // This ensures that "stasjon" has a minWidth. It also overrides Spor's default minWidth which renders the table useless for small widths
                minWidth={tableWidthPx}
              >
                <TableHeader
                  backgroundColor={theme.colorBackgroundSecondary}
                  position="sticky"
                  top="0"
                  zIndex={1}
                >
                  <TrainStopHeader />
                </TableHeader>
                <TableBody>
                  <TrainStopContent data={data} fillRate={fillRate} />
                </TableBody>
              </TableXS>
            </Box>
          );
        }
        return <NoDataMessage reason={t("Fant ingen rute for dette toget.")} />;
      }
      case "error":
        return <FailureMessage />;
    }
  };

  return (
    <HeadedContainer
      panelStyle={SharedGridStyleNoPadding}
      variant="main"
      headerHeight="54px"
      header={<TrainRouteHeader delay={delay} />}
    >
      {getContent()}
    </HeadedContainer>
  );
};

export default withErrorBoundary(TrainRoute);
